import { type FC, useEffect, useState } from 'react'
import cn from 'classnames'
import { useTranslation, Trans } from '@egw/i18n'
import { EvergreenWealthLogo } from '@egw/assets'
import { Link } from '@egw/components'
import { configClient } from '@egw/config'
import { CoreExternalRoutes, type FeatureFlags } from '@egw/constants'
import { checkInAppBrowser } from '@egw/auth/src/utils'
import { useSearchParams } from 'react-router'
import { useFlags } from '@egw/feature-flags'
import { WealthDisclosures } from '@egw/features-system'
import { type Config } from '../../config/config'
import { SignInForm, SignInHelp } from '../../components'
import { BrowserErrorPage as BrowserError } from '../browser-error'
import styles from './index.module.css'

export const SignInPage: FC = () => {
  const { flags } = useFlags<FeatureFlags>()

  return flags.rolloutSignInPageRedesign ? <SignInPageCompRedesign /> : <SignInPageComp />
}

export const SignInPageComp: FC = () => {
  const [isError, setIsError] = useState(false)
  const [isHelp, setIsHelp] = useState(false)
  const [isInAppBrowser, setIsInAppBrowser] = useState<boolean>(false)
  const { t } = useTranslation('features-auth')
  const config = configClient.getConfig() as Config
  const [searchParams] = useSearchParams()

  const sessionTimeout = Boolean(searchParams.get('session-timeout') === 'true')
  const recoveryExpired = Boolean(searchParams.get('recoveryExpired') === 'true')
  const { flags } = useFlags<FeatureFlags>()

  useEffect(() => {
    if (typeof window === 'undefined') return
    setIsInAppBrowser(checkInAppBrowser())
  }, [])

  return isInAppBrowser ? (
    <BrowserError />
  ) : (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="bg-cover h-[478px] mb-24">
        {!isHelp && (
          <SignInForm
            onError={(value) => {
              setIsError(value)
            }}
            onHelp={() => {
              setIsHelp(true)
            }}
            sessionTimeout={Boolean(sessionTimeout)}
            recoveryExpired={recoveryExpired}
          />
        )}

        {isHelp ? (
          <SignInHelp
            onBack={() => {
              setIsHelp(false)
              setIsError(false)
            }}
          />
        ) : null}
      </div>

      <div className="flex-1" />

      <div
        className={cn('pt-12 pb-8 bg-gray-100', {
          'mt-40': isHelp,
          'mt-60': (isError || sessionTimeout || recoveryExpired) && !isHelp,
          'mt-80': sessionTimeout && isError,
          'mt-36': !isError && !isHelp && !sessionTimeout && !recoveryExpired,
        })}
      >
        <div className={`flex flex-col gap-2 ${styles.container} text-xs text-meta text-left mx-auto`}>
          <WealthDisclosures />
        </div>
      </div>
    </div>
  )
}

export const SignInPageCompRedesign: FC = () => {
  const [isHelp, setIsHelp] = useState(false)
  const [isInAppBrowser, setIsInAppBrowser] = useState<boolean>(false)
  const [searchParams] = useSearchParams()

  const sessionTimeout = Boolean(searchParams.get('session-timeout') === 'true')
  const recoveryExpired = Boolean(searchParams.get('recoveryExpired') === 'true')

  useEffect(() => {
    if (typeof window === 'undefined') return
    setIsInAppBrowser(checkInAppBrowser())
  }, [])

  return isInAppBrowser ? (
    <BrowserError />
  ) : (
    <div className="h-full flex flex-col">
      {!isHelp && (
        <SignInForm
          onHelp={() => {
            setIsHelp(true)
          }}
          sessionTimeout={Boolean(sessionTimeout)}
          recoveryExpired={recoveryExpired}
        />
      )}

      {isHelp ? (
        <SignInHelp
          onBack={() => {
            setIsHelp(false)
          }}
        />
      ) : null}

      <div className="py-20 md:py-2xl">
        <div data-testid="evergreen-disclosure" className={`${styles.container} typo-primary-sm text-meta mx-auto`}>
          <EvergreenWealthLogo className="w-[250px] text-gray-900" />
          <hr className="border-gray-600 my-2xl" />
          <div className="flex flex-col gap-3">
            <WealthDisclosures />
          </div>
        </div>
      </div>
    </div>
  )
}
